import { Component, ViewEncapsulation, OnInit, AfterViewInit } from '@angular/core';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { UserService } from 'src/app/services/user/user.service';
import { UserModalService } from 'src/app/services/user-modal/user-modal.service';
import { User, PaginationInfo, PaginatedResponse } from 'src/app/shared/models';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { USER_TYPES } from 'src/app/config/system.config';
import Swal from 'sweetalert2';
import { GlobalStateService } from 'src/app/services/global/global-state.service';
declare var KTMenu: any;
declare var bootstrap: any;

@Component({
  selector: 'app-cm-users-list',
  templateUrl: './cm-users-list.component.html',
  styleUrls: ['./cm-users-list.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class CmUsersListComponent extends DestructorComponent implements OnInit, AfterViewInit {

  // Data properties
  users: User[] = [];
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // State properties
  isLoading: boolean = false;
  searchTerm: string = '';
  currentPage: number = 1;
  showFiltersForm = false;
  primaryAgentOptions: any[] = [];

  // Filter properties
  filters = {
    is_primary_agent: '',
    primary_agent: '',
    search: ''
  };

  // Bulk delete properties
  selectedUserIds: Set<string> = new Set<string>();
  isDeleting: boolean = false;

  loggedInUser:any;

  // Search subject for debouncing
  private searchSubject = new Subject<string>();

  constructor(
    private userService: UserService,
    private userModalService: UserModalService,
    private _globalStateService: GlobalStateService
  ) {
    super();
    this.setupSearchDebounce();
  }

  ngOnInit(): void {
    this.loadUsers();
    this.getPrimaryAgents();

    this.subscriptions.push(
      this._globalStateService.user.subscribe(user => {
        this.loggedInUser = user;
      })
    )
  }

  private setupSearchDebounce(): void {
    this.subscriptions.push(
      this.searchSubject.pipe(
        debounceTime(300),
        distinctUntilChanged()
      ).subscribe(searchTerm => {
        this.searchTerm = searchTerm;
        this.currentPage = 1;
        this.loadUsers();
      })
    );
  }


  loadUsers(page: number = this.currentPage, search: string = this.searchTerm): void {
    this.isLoading = true;
    this.currentPage = page;

    // Build filters object
    const apiFilters: any = { role_type: USER_TYPES.ADMIN };

    // Add filter parameters if they exist
    if (this.filters.is_primary_agent) {
      apiFilters.is_primary_agent = this.filters.is_primary_agent === 'primary';
    }
    if (this.filters.primary_agent) {
      apiFilters.primary_agent = this.filters.primary_agent;
    }
    if (this.filters.search) {
      search = this.filters.search;
    }

    this.subscriptions.push(
      this.userService.GetData(page.toString(), search, apiFilters).subscribe({
        next: (response: PaginatedResponse<User>) => {
          if (response.status && response.data) {
            this.users = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
            this.refreshMenus(); // Add this line
          } else {
            this.users = [];
            this.resetPagination();
          }
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading users:', error);
          this.users = [];
          this.resetPagination();
          this.isLoading = false;
        }
      })
    );
  }

  onPageChange(page: number): void {
    this.loadUsers(page);
  }

  onSearch(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchSubject.next(target.value);
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.searchSubject.next('');
  }

  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 20,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  // Utility methods for template
  getAvatarDisplay(user: User): { type: 'image' | 'initial', value: string, bgClass?: string } {
    if (user.avatar && user.avatar.trim() !== '') {
      return { type: 'image', value: user.avatar };
    }

    const name = user.name || user.email || 'U';
    const initial = name.charAt(0).toUpperCase();
    const bgClasses = ['bg-light-primary text-primary', 'bg-light-success text-success',
      'bg-light-info text-info', 'bg-light-warning text-warning',
      'bg-light-danger text-danger'];
    const bgClass = bgClasses[initial.charCodeAt(0) % bgClasses.length];

    return { type: 'initial', value: initial, bgClass };
  }

  ngAfterViewInit(): void {
    // Initialize KTMenu instances for dropdown menus
    this.initializeMenus();
  }

  private initializeMenus(): void {
    // Wait for DOM to be ready, then initialize menus
    setTimeout(() => {
      if (typeof KTMenu !== 'undefined') {
        KTMenu.createInstances();
      }
    }, 100);
  }

  // Call this after users data is loaded
  private refreshMenus(): void {
    setTimeout(() => {
      if (typeof KTMenu !== 'undefined') {
        KTMenu.createInstances();
      }
    }, 50);
  }

  trackByUserId(index: number, user: User): string {
    return user._id || user.doc_id || index.toString();
  }

  // Add method to handle edit user
  editUser(user: User): void {
    // Use the service to set the selected user
    this.userModalService.setSelectedUser(user);
    // Also dispatch custom event as backup
    const editEvent = new CustomEvent('editUser', {
      detail: user,
      bubbles: true
    });
    console.log('Dispatching editUser event:', editEvent);
    document.dispatchEvent(editEvent);

    // Small delay to ensure data is set
    setTimeout(() => {
      // Open the modal
      const modalElement = document.getElementById('kt_modal_update_details');
      console.log('Modal element found:', modalElement);
      if (modalElement) {
        const modal = new bootstrap.Modal(modalElement);
        modal.show();
      }
    }, 100);
  }


  verifyUser(user: User): void {

    // Use the service to set the selected user
    Swal.fire({
      title: 'Are you sure?',
      text: `You want to verify user "${user.name || user.email || ' this user'}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, verify it!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performVerify(user);
      }
    });

  }

  performVerify(user: User): void {

    this.subscriptions.push(
      this.userService.VerifyUser(user._id || user.doc_id || '').subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Verified!',
              text: 'User has been verified successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.loadUsers(this.currentPage, this.searchTerm);
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to verify user.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error) => {
          console.error('Error verifying user:', error);
          Swal.fire({
            title: 'Error!',
            text: 'Failed to verify user. Please try again.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );

  }
  // Handle user updated event
  onUserUpdated(): void {
    this.loadUsers(); // Refresh the users list
  }

  // Handle user added event
  onUserAdded(): void {
    this.loadUsers(); // Refresh the users list
  }

  // Filter methods
  toggleFiltersForm(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  getPrimaryAgents(): void {
    this.subscriptions.push(
      this.userService.GetData("1", "", { paginate: 'false', is_primary_agent: true, role_type: 30 }).subscribe({
        next: (res: any) => {
          if (res.status && res.data) {
            const agents = Array.isArray(res.data) ? res.data : res.data.data || [];
            this.primaryAgentOptions = agents.map((agent: any) => ({
              value: agent._id || agent.doc_id || '',
              label: `${agent.title || ''} ${agent.name || agent.login_id || 'Unknown Agent'}`.trim()
            }));
          } else {
            this.primaryAgentOptions = [];
          }
        },
        error: (error) => {
          console.error('Error fetching primary agents:', error);
          this.primaryAgentOptions = [];
        }
      })
    );
  }

  applyFilters(): void {
    this.currentPage = 1; // Reset to first page when applying filters
    this.loadUsers();
  }

  clearAllFilters(): void {
    this.filters = {
      is_primary_agent: '',
      primary_agent: '',
      search: ''
    };
    this.currentPage = 1;
    this.loadUsers();
  }

  // Bulk delete methods
  toggleSelection(userId: string): void {
    if (this.selectedUserIds.has(userId)) {
      this.selectedUserIds.delete(userId);
    } else {
      this.selectedUserIds.add(userId);
    }
  }

  toggleSelectAll(): void {
    // When using click event, the checked state hasn't changed yet, so we check the opposite
    const willBeChecked = !this.isAllSelected;

    if (willBeChecked) {
      this.users.forEach(user => {
        const userId = user._id || user.doc_id || '';
        if (userId) {
          this.selectedUserIds.add(userId);
        }
      });
    } else {
      this.selectedUserIds.clear();
    }
  }

  isSelected(userId: string): boolean {
    return this.selectedUserIds.has(userId);
  }

  get isAllSelected(): boolean {
    return this.users.length > 0 &&
           this.users.every(user => this.selectedUserIds.has(user._id || user.doc_id || ''));
  }

  get hasSelectedItems(): boolean {
    return this.selectedUserIds.size > 0;
  }

  onBulkDelete(): void {
    if (this.selectedUserIds.size === 0) {
      return;
    }

    const count = this.selectedUserIds.size;
    const itemText = count > 1 ? 'users' : 'user';

    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete ${count} ${itemText}. This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performBulkDelete();
      }
    });
  }

  private performBulkDelete(): void {
    this.isDeleting = true;
    const idsArray = Array.from(this.selectedUserIds);

    console.log('Selected user IDs for deletion:', idsArray);

    this.subscriptions.push(
      this.userService.BulkDelete(idsArray).subscribe({
        next: (response: any) => {
          this.isDeleting = false;
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: `${idsArray.length} user(s) have been deleted successfully.`,
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              // Clear selection and reload users
              this.selectedUserIds.clear();
              this.loadUsers(this.currentPage, this.searchTerm);
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to delete users.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error) => {
          this.isDeleting = false;
          console.error('Error deleting users:', error);
          Swal.fire({
            title: 'Error!',
            text: 'Failed to delete users. Please try again.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }
}
